import {type AxisZoomIntent, type UserZoomIntentPayload} from '../types';

/**
 * Asymmetric preserve/clear behavior for single-axis zooms (PRODUCT_RULES.md
 * sections 1.2 and 2.2):
 *   - Y-only zoom preserves the existing x bounds (Box-Zoom-shaped result).
 *   - X-only zoom clears the existing y bounds (chart rescales y to data).
 *
 * If PRESERVE_X_ON_Y_ONLY_ZOOM is flipped back to false, `handleUserZoomChange`
 * in PanelZoomContext.tsx must also clear the queryZoom on a y-only gesture to
 * keep userZoom and queryZoom in sync. The reducer path in usePanelZoomReducer
 * derives queryZoom from userZoom, so it stays consistent automatically.
 */
const PRESERVE_X_ON_Y_ONLY_ZOOM = true;
const PRESERVE_Y_ON_X_ONLY_ZOOM = false;

export const ZOOM_KEYS = [
  'xAxisMin',
  'xAxisMax',
  'yAxisMin',
  'yAxisMax',
] as const;
type ZoomKey = (typeof ZOOM_KEYS)[number];
type ZoomState = Partial<Record<ZoomKey, number | null | undefined>>;
export type UserZoomPatch = Partial<Record<ZoomKey, number | undefined>>;

export function getZoomIntent(zoom: UserZoomIntentPayload): AxisZoomIntent {
  if (zoom.intent != null) {
    return zoom.intent;
  }

  return {
    shouldZoomX: zoom.xAxisMin != null || zoom.xAxisMax != null,
    shouldZoomY: zoom.yAxisMin != null || zoom.yAxisMax != null,
  };
}

export function isResetZoomIntent(zoom: UserZoomIntentPayload): boolean {
  const intent = getZoomIntent(zoom);
  return !intent.shouldZoomX && !intent.shouldZoomY;
}

export function shouldPropagateSharedXAxisZoom(
  zoom: UserZoomIntentPayload
): boolean {
  return getZoomIntent(zoom).shouldZoomX || isResetZoomIntent(zoom);
}

/**
 * Patch semantics:
 * - omitted keys preserve the existing bound
 * - number values set the bound
 * - present `undefined` values clear the bound
 */
export function buildUserZoomPatch(zoom: UserZoomIntentPayload): UserZoomPatch {
  const intent = getZoomIntent(zoom);

  if (!intent.shouldZoomX && !intent.shouldZoomY) {
    return {
      xAxisMin: undefined,
      xAxisMax: undefined,
      yAxisMin: undefined,
      yAxisMax: undefined,
    };
  }

  const patch: UserZoomPatch = {};

  if (intent.shouldZoomX) {
    patch.xAxisMin = zoom.xAxisMin;
    patch.xAxisMax = zoom.xAxisMax;
  } else if (!PRESERVE_X_ON_Y_ONLY_ZOOM) {
    patch.xAxisMin = undefined;
    patch.xAxisMax = undefined;
  }

  if (intent.shouldZoomY) {
    patch.yAxisMin = zoom.yAxisMin;
    patch.yAxisMax = zoom.yAxisMax;
  } else if (!PRESERVE_Y_ON_X_ONLY_ZOOM) {
    patch.yAxisMin = undefined;
    patch.yAxisMax = undefined;
  }

  return patch;
}

export function isZoomActive(zoom: ZoomState): boolean {
  return ZOOM_KEYS.some(key => zoom[key] != null);
}
